import type { ReactEventHandler } from 'react';
import { useState } from 'react';

// When values are valid.
interface ValidState<T> {
  values: T;
  errors: object;
}

// When errors occurs.
interface ErrorState<T> {
  values: object;
  errors: {
    [k in keyof T]: string[];
  };
}

// Array of validation functions for each key.
type Validator<T> = {
  [k in keyof T]: ((p: T[k]) => string | undefined)[];
};

// Array of validation functions for each key and all values 
// (For fields that are displayed in particular conditions).
type ValidatorWithAllValues<T> = {
  [k in keyof T]: ((p: T[k], all: T) => string | undefined)[];
};

// Result of functions validations.
type Validation<T> = {
  [k in keyof T]: string[];
};

// Form state hook.
export function useForm<T>(
  initalvalues: T,
  validator: (value: T) => ValidState<T> | ErrorState<T>,
  callback: (data: T) => Promise<void>
) {
  const [values, setValues] = useState<T>(initalvalues);
  const [validationState, setValidationState] = useState<
    ValidState<T> | ErrorState<T>
  >({ values: initalvalues, errors: {} }); // inital values never validated.
  const [hasSubmitted, setHasSubmitted] = useState(false); // If the user submit at least once.

  /**
   * Stores input values for a single field.
   */
  function setValue<U extends keyof T, V extends T[U]>(key: U, value: V) {
    const newValues = { ...values, [key]: value };
    setValues(newValues);
    if (hasSubmitted) {
      const newValidationState = validator(newValues);
      setValidationState(newValidationState);
    }
  }

  /**
   * Store input values for any number of fields.
   */
  function setMultipleValues(partial: Partial<T>) {
    const newValues = { ...values, ...partial };
    setValues(newValues);
    if (hasSubmitted) {
      const newValidationState = validator(newValues);
      setValidationState(newValidationState);
    }
  }

  /**
   * Retrieves input values for a single field.
   */
  function watch<U extends keyof T, V extends T[U]>(key: U): V {
    return values[key] as V;
  }

  // Retrieves input values for all fields.
  function watchAll(): T {
    return values;
  }

  /**
   * Restore values and validation state to default.
   */
  function reset() {
    setValues(initalvalues);
    setHasSubmitted(false);
    setValidationState({ values: initalvalues, errors: {} });
  }

  /**
   * Manage form submission.
   */
  const onSubmit: ReactEventHandler = (e) : boolean => {
    setHasSubmitted(true);
    e.preventDefault();
    const newValidationState = validator(values);

    // Manage case of submission without entering anything.
    setValidationState(newValidationState);

    // Proceed only if there isn't any validation error.
    if (
      newValidationState !== undefined &&
      newValidationState.errors !== undefined &&
      Object.keys(newValidationState.errors).length === 0
    )
      callback(values);
     // unstantenly return errors.
      const hasErrors = Object.keys(newValidationState.errors).length > 0;
      return hasErrors;
  };

  /**
   * Get errors for each key.
   */
  function getErrors<k extends keyof T>(key: k): string[] {
    if (validationState === undefined) {
      return [];
    }
    if (
      Object.keys(validationState.errors).includes(key as unknown as string)
    ) {
      return (validationState.errors as Record<keyof T, string[]>)[key];
    }
    return [];
  }

  return {
    setValue,
    watch,
    watchAll,
    onSubmit,
    reset,
    getErrors,
    setMultipleValues,
    hasSubmitted,
  };
}

/**
 * Create a validation function for each key. 
 * This method is used when all values in the form are independent 
 * of each other and we do not need to add conditions depending on 
 * the selected values.
 */
export const createValidator = <T>(params: Validator<T>) => {
  return (values: T): ValidState<T> | ErrorState<T> => {
    const acc = {} as Validation<T>;

    // Exucute every validation function for each key, filtering out undefined results.
    Object.keys(params).forEach((key) => {
      acc[key as keyof T] = params[key as keyof T]
        .map((f) => f(values[key as keyof T]))
        .filter((value) => value !== undefined);
    });

    // Delete all fields without errors from acc.
    Object.keys(params).forEach((key) => {
      if (acc[key as keyof T].length === 0) {
        delete acc[key as keyof T];
      }
    });

    if (Object.keys(acc).length === 0) {
      return {
        values,
        errors: {},
      };
    } else {
      return {
        values: {},
        errors: acc,
      };
    }
  };
};

/**
 * Create a validation function for each key with all values of form for the forms,
 * that have condition for fields, whicth are required.
 */
export const createValidatorWithAllValues = <T>(params: ValidatorWithAllValues<T>) => {
  return (values: T): ValidState<T> | ErrorState<T> => {
    const acc = {} as Validation<T>;

    // Exucute every validation function for each key, filtering out undefined results.
    Object.keys(params).forEach((key) => {
      acc[key as keyof T] = params[key as keyof T]
        .map((f) => f(values[key as keyof T], values))
        .filter((value) => value !== undefined);
    });

    // Delete all fields without errors from acc.
    Object.keys(params).forEach((key) => {
      if (acc[key as keyof T].length === 0) {
        delete acc[key as keyof T];
      }
    });

    if (Object.keys(acc).length === 0) {
      return {
        values,
        errors: {},
      };
    } else {
      return {
        values: {},
        errors: acc,
      };
    }
  };
};
